import React from "react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { TwoColumnLayout } from "@/components/TwoColumnLayout";

/**
 * Props for the TwoColumnSheet component.
 * Provides a full-screen sheet with a two-column layout, header, and footer.
 */
interface TwoColumnSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  header?: React.ReactNode;
  leftColumn: React.ReactNode;
  rightColumn: React.ReactNode;
  footer?: React.ReactNode;
  /** Optional panel rendered to the left of the two-column grid (e.g. assembly tree sidebar). */
  sidePanel?: React.ReactNode;
  leftColumnSize?: "quarter" | "third" | "half" | "full";
  /** When true, renders a draggable resize handle between the left and right columns. */
  resizable?: boolean;
  /** Unique key for persisting the resizable panel sizes to localStorage. */
  autoSaveId?: string;
  /** When true, displays a centered loading indicator instead of the two-column layout */
  loading?: boolean;
  /** Custom loading text to display below the spinner */
  loadingText?: string;
}

/**
 * Full-screen sheet wrapping a TwoColumnLayout.
 */
export const TwoColumnSheet: React.FC<TwoColumnSheetProps> = ({
  open,
  onOpenChange,
  ...layoutProps
}) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-screen h-screen max-w-full sm:max-w-full p-0 flex flex-col">
        <VisuallyHidden asChild>
          <SheetTitle>Details Panel</SheetTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <SheetDescription>
            A two-column panel for viewing and editing details
          </SheetDescription>
        </VisuallyHidden>
        <TwoColumnLayout onClose={() => onOpenChange(false)} {...layoutProps} />
      </SheetContent>
    </Sheet>
  );
};
